import type { TranslationJobScope } from '@/shared/constants/status'
import type { LocalizedEditorFieldConfig } from '@/shared/ui/localized-fields-editor'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LocalizedTranslationJobSection } from '@/features/localized-translation'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'

export interface ISurveyTranslationRow {
  language: string
  name: string
  description: string
}

interface SurveyTranslationSectionProps {
  languages: string[]
  translations: ISurveyTranslationRow[]
  onChange: (rows: ISurveyTranslationRow[]) => void
  onGenerationStateChange?: (hasPendingGeneration: boolean) => void
  entityId: string
  entityLabel?: string
  entityPath?: string
  scope?: TranslationJobScope
  blockTitle?: string
  fields?: LocalizedEditorFieldConfig<'name' | 'description'>[]
}

/**
 * Wrapper dịch localized content cho Survey.
 *
 * Survey dùng cùng background job workflow với Tour/POI:
 * sửa name/description -> enqueue job -> Background Jobs hoàn tất -> apply kết quả về form.
 */
export function SurveyTranslationSection({
  languages,
  translations,
  onChange,
  onGenerationStateChange,
  entityId,
  entityLabel,
  entityPath,
  scope = TRANSLATION_JOB_SCOPE.SURVEY,
  blockTitle,
  fields: fieldsProp,
}: SurveyTranslationSectionProps) {
  const { t } = useTranslation()
  const editorFields = useMemo<LocalizedEditorFieldConfig<'name' | 'description'>[]>(
    () =>
      fieldsProp ?? [
        {
          key: 'name',
          label: t('survey.fieldTitle', { defaultValue: 'Title' }),
          placeholder: t('survey.fieldTitlePlaceholder', { defaultValue: 'Survey title' }),
        },
        {
          key: 'description',
          label: t('survey.fieldDescription', { defaultValue: 'Description' }),
          placeholder: t('survey.fieldDescriptionPlaceholder', {
            defaultValue: 'Description',
          }),
          multiline: true,
          rows: 4,
        },
      ],
    [fieldsProp, t],
  )

  return (
    <LocalizedTranslationJobSection<ISurveyTranslationRow>
      languages={languages}
      translations={translations}
      onChange={onChange}
      scope={scope}
      entityId={entityId}
      {...(entityLabel ? { entityLabel } : {})}
      {...(entityPath ? { entityPath } : {})}
      audioEnabled={false}
      requiredMessage={t('survey.translateSourceRequired', {
        defaultValue: 'Enter title and description in the current language first.',
      })}
      {...(blockTitle ? { blockTitle } : {})}
      fields={() => editorFields}
      translateLabel={t('poi.localizedSpeech.generateSelectedTextOnly')}
      scopeHint={t('survey.translateScopeHint', {
        defaultValue:
          'Translate title and description from the current language to selected languages.',
      })}
      loadingText={t('poi.localizedSpeech.generatingTextOnly')}
      {...(onGenerationStateChange ? { onGenerationStateChange } : {})}
    />
  )
}
